// apps/nos/client/src/lib/eventLogger.ts
// Public call-site API for Amplitude analytics on the client.
//
// USAGE
//   import { logEvent } from "@/lib/eventLogger";
//   logEvent("flavor_create_submitted", { path: "have-target", base_preset_count: 3 });
//
// Identity (clerk_user_id, org_id, org_kind, email) is auto-attached by the
// useAmplitudeUser hook — call sites never pass it.

import { amplitude, amplitudeEnabled, amplitudeEnvironment } from "./amplitudeInit";

export type EventProps = Record<
  string,
  string | number | boolean | null | undefined
>;

export type UserContext = {
  clerkUserId?: string;
  orgId?: string;
  orgKind?: "personal" | "shared";
  email?: string;
  name?: string;
  username?: string;
};

/** Fire an Amplitude event. Always logs to console for dev visibility. */
export function logEvent(name: string, props?: EventProps): void {
  // Inject `environment` LAST so a caller can't accidentally overwrite it.
  const cleanProps: EventProps = {
    ...(props ? stripUndefined(props) : {}),
    environment: amplitudeEnvironment,
  };
  console.log(`[AMPL] ${name}`, cleanProps);
  if (!amplitudeEnabled) return;
  try {
    amplitude.track(name, cleanProps);
  } catch (err) {
    console.warn(`[AMPL] track failed for ${name}`, err);
  }
}

/**
 * Push the current user identity into Amplitude. Called by useAmplitudeUser
 * whenever Clerk identity changes; call sites should NOT invoke this directly.
 */
export function setUserContext(ctx: UserContext): void {
  if (!amplitudeEnabled) return;
  try {
    // user_id is the Clerk user id — stable + immutable. Identifying
    // info (email, name) is set as user properties below; that makes
    // them visible AND searchable in Amplitude's User Lookup.
    if (ctx.clerkUserId) amplitude.setUserId(ctx.clerkUserId);
    const id = new amplitude.Identify();
    if (ctx.clerkUserId) id.set("clerk_user_id", ctx.clerkUserId);
    if (ctx.orgId) id.set("org_id", ctx.orgId);
    if (ctx.orgKind) id.set("org_kind", ctx.orgKind);
    if (ctx.email) {
      id.set("email", ctx.email);
      // Mirror the email as the default display name so the user is
      // recognizable in Amplitude's User Lookup at a glance. Overwritten
      // by `name` below if Clerk gave us a real full name.
      id.set("name", ctx.email);
    }
    if (ctx.name) id.set("name", ctx.name);
    if (ctx.username) id.set("username", ctx.username);
    amplitude.identify(id);
  } catch (err) {
    console.warn(`[AMPL] setUserContext failed`, err);
  }
}

/** Drop identity. Called by useAmplitudeUser on sign-out. */
export function clearUserContext(): void {
  if (!amplitudeEnabled) return;
  try {
    amplitude.reset();
  } catch (err) {
    console.warn(`[AMPL] clearUserContext failed`, err);
  }
}

function stripUndefined(o: EventProps): EventProps {
  const out: EventProps = {};
  for (const [k, v] of Object.entries(o)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}
